import { Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Alert, Button, EmptyState, Spinner } from '@/components/ui'
import { useSession } from '@/features/auth/session'
import { useCurrentTenant } from '@/features/tenants/useCurrentTenant'

import { InviteSection } from './components/InviteSection'
import { MembersTable } from './components/MembersTable'
import { useTeamMembers } from './queries'

/**
 * Gestao da equipe: membros (nome/e-mail/papel, alterar e remover) e convites por e-mail com link.
 * Quem nao tem a capability `team.manage` recebe uma mensagem honesta (a lista volta null/403), nao
 * uma tela quebrada. Vocabulario neutro do produto.
 */
export function TeamPage() {
  const { t } = useTranslation()
  const { user } = useSession()
  const { tenant } = useCurrentTenant()
  const membersQuery = useTeamMembers(tenant?.id)

  if (tenant === null || membersQuery.isLoading) {
    return (
      <div className="flex h-full items-center justify-center py-12">
        <Spinner label={t('loading')} />
      </div>
    )
  }

  if (membersQuery.isError) {
    return (
      <div className="flex h-full items-center justify-center py-12">
        <Alert variant="danger" title={t('error')} className="max-w-lg">
          <Button
            variant="secondary"
            size="sm"
            className="mt-2"
            onClick={() => {
              void membersQuery.refetch()
            }}
          >
            {t('retry')}
          </Button>
        </Alert>
      </div>
    )
  }

  const members = membersQuery.data

  // `null` = sem a capability team.manage: mostra o motivo, sem controles de gestao.
  if (members === null || members === undefined) {
    return (
      <div className="flex h-full items-center justify-center py-12">
        <EmptyState
          icon={Users}
          title={t('team.noPermission.title')}
          description={t('team.noPermission.description')}
          className="max-w-lg"
        />
      </div>
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold text-foreground-primary">{t('team.title')}</h1>
        <p className="text-sm text-foreground-muted">{t('team.subtitle')}</p>
      </header>

      <InviteSection tenantId={tenant.id} />

      <MembersTable tenantId={tenant.id} currentUserId={user?.id ?? null} members={members} />
    </div>
  )
}
